import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRandomPromoTrips, type PromoTripWithMap } from "../../api/admin";
import { Avatar } from "../../components/common/Avatar";
import { StaticPromoMap } from "../../components/map/StaticPromoMap";
import { withWidth } from "../../lib/photoUrl";
import { useConsent } from "../../utils/cookieConsent";

function formatRoute(trip: PromoTripWithMap): string {
  const parts = [trip.startLocation, trip.endLocation].filter(Boolean);
  if (parts.length >= 2) return parts.join(" → ");
  return trip.title;
}

export function PromoCard({ trip }: { trip: PromoTripWithMap }) {
  const { t } = useTranslation("trips");
  const [flipped, setFlipped] = useState(false);
  const consentAccepted = useConsent();

  return (
    <div
      className={`promo-card ${flipped ? "is-flipped" : ""}`}
      data-testid={`example-card-${trip.tokenId}`}
    >
      <a className="promo-card__photo-link" href={`/share/${trip.tokenId}`} aria-label={trip.title}>
        <div className="promo-card__photo">
          {trip.coverPhoto ? (
            <img
              src={withWidth(trip.coverPhoto, 480)!}
              alt=""
              className="promo-card__photo-img"
              loading="lazy"
              width={600}
              height={240}
            />
          ) : (
            <div className="promo-card__photo-placeholder" />
          )}
        </div>
      </a>
      <div className="promo-card__map-pane">
        {consentAccepted && (
          <StaticPromoMap
            trip={trip}
            alt={formatRoute(trip)}
            width={400}
            height={450}
          />
        )}
      </div>
      <a className="promo-card__body-link" href={`/share/${trip.tokenId}`}>
        <div className="promo-card__body">
          <h3 className="promo-card__title">{trip.title}</h3>
          {trip.ownerName && (
            <div className="promo-card__contributors">
              <Avatar name={trip.ownerName} image={withWidth(trip.ownerImage, 48)} size={22} />
              <span className="promo-card__contributors-name">
                {trip.ownerName.split(/\s+/)[0]}
              </span>
            </div>
          )}
          <p className="promo-card__route">{formatRoute(trip)}</p>
          <div className="promo-card__meta">
            {trip.days != null && trip.days > 0 && <span>{trip.days} days</span>}
            {trip.stops > 0 && <span>{trip.stops} stops</span>}
          </div>
        </div>
      </a>
      <button
        type="button"
        className="promo-card__flip-toggle"
        data-testid={`promo-flip-${trip.tokenId}`}
        aria-label={flipped ? t("examples.backToDetails") : t("examples.viewOnMap")}
        onClick={() => setFlipped((f) => !f)}
      >
        {flipped ? (
          <><ChevronLeftIcon /><span>{t("examples.backToDetails")}</span></>
        ) : (
          <><MapIcon /><span>{t("examples.viewOnMap")}</span></>
        )}
      </button>
    </div>
  );
}

function ReloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export function ExamplesSection() {
  const { t } = useTranslation("trips");
  const [trips, setTrips] = useState<PromoTripWithMap[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRandomPromoTrips(8);
      setTrips(result);
    } catch {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  if (trips !== null && trips.length === 0) return null;

  return (
    <section className="home-examples" data-testid="home-examples">
      <div className="home-examples__header">
        <div className="home-examples__header-row">
          <div>
            <p className="home-examples__eyebrow">{t("examples.eyebrow")}</p>
            <h2 className="home-examples__headline">{t("examples.headline")}</h2>
          </div>
          <button
            type="button"
            className="home-examples__reload"
            data-testid="examples-reload"
            aria-label={t("examples.loadNew")}
            onClick={fetchTrips}
            disabled={loading}
          >
            <ReloadIcon />
          </button>
        </div>
      </div>
      <div className="home-examples__grid">
        {trips === null
          ? Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="promo-card-skeleton" aria-hidden="true" />
            ))
          : trips.map((trip) => (
              <PromoCard key={trip.tokenId} trip={trip} />
            ))}
      </div>
    </section>
  );
}
